import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { Board, BoardDetail, BoardUpdatePayload, UUID } from "@/lib/types";

interface Vars {
  boardId: UUID;
  payload: BoardUpdatePayload;
  // Needed so we can invalidate the workspace-scoped list cache.
  workspaceId: UUID;
}

export function useUpdateBoard() {
  const qc = useQueryClient();
  return useMutation<Board, Error, Vars>({
    mutationFn: ({ boardId, payload }) => api.updateBoard(boardId, payload),
    onSuccess: (board, { workspaceId }) => {
      qc.setQueryData<Board[]>(queryKeys.boards(workspaceId), (prev) =>
        prev ? prev.map((b) => (b.id === board.id ? { ...b, ...board } : b)) : prev,
      );
      qc.setQueryData<Board[]>(queryKeys.myBoards, (prev) =>
        prev ? prev.map((b) => (b.id === board.id ? { ...b, ...board } : b)) : prev,
      );
      // Pinned boards: keep names + flags in sync when the board is pinned.
      qc.setQueryData<Board[]>(queryKeys.pinnedBoards, (prev) =>
        prev ? prev.map((b) => (b.id === board.id ? { ...b, ...board } : b)) : prev,
      );
      // BoardDetail for the open board page — the header/breadcrumb reads the name
      // from this query, so renaming on the board page needs to reflect immediately.
      qc.setQueryData<BoardDetail>(queryKeys.board(board.id), (prev) =>
        prev ? { ...prev, ...board } : prev,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.boards(workspaceId) });
      void qc.invalidateQueries({ queryKey: queryKeys.myBoards });
      void qc.invalidateQueries({ queryKey: queryKeys.pinnedBoards });
    },
  });
}
